import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import ContactForm from '../components/contact/ContactForm';
import ContactInfo from '../components/contact/ContactInfo';
import { Phone, Clock, MapPin, Award, Wrench, Droplet, CheckCircle, XCircle, DollarSign, Shield, AlertTriangle, Flame, Home, Users, Gauge, ShieldCheck, UserCheck, Thermometer, Star, ArrowRight, FileText, Search, AlertCircle, TrendingUp, ChevronDown, ExternalLink } from 'lucide-react';
import { SchemaInjector, createLocalBusinessSchema, createServiceSchema, createBreadcrumbSchema } from '../components/SchemaInjector';

const UpfrontPlumbingPricesDenver = () => {
  // BirdEye widget loading
  useEffect(() => {
    const loadBirdEyeWidget = () => {
      // Check if script already exists
      const existingScript = document.querySelector('#birdeye-widget-script');
      if (existingScript) return;

      // Create and load the BirdEye script
      const script = document.createElement('script');
      script.id = 'birdeye-widget-script';
      script.type = 'text/javascript';
      script.src = 'https://birdeye.com/embed/v7/172384402926614/8/9876543218815183';
      script.async = true;
      
      script.onload = () => {
        console.log('BirdEye widget loaded successfully');
      };
      
      script.onerror = () => {
        console.error('Failed to load BirdEye widget');
        // Show fallback content
        const fallback = document.querySelector('#birdeye-fallback') as HTMLElement;
        if (fallback) fallback.style.display = 'block';
      };

      document.head.appendChild(script);
    };

    loadBirdEyeWidget();

    // Cleanup on unmount
    return () => {
      const script = document.querySelector('#birdeye-widget-script');
      if (script) script.remove();
    };
  }, []);

  // SEO Meta Tags
  useEffect(() => {
    document.title = "Honest, Upfront Plumbing Prices in Denver, Littleton | Colorado First";
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'See upfront plumbing prices online for Denver, Littleton, Arvada & more. We send expert plumbers, not commissioned salespeople. View our transparent pricing philosophy.');
    }
  }, []);

  // Smooth scroll to service cards
  const scrollToServiceCards = (e: React.MouseEvent) => {
    e.preventDefault();
    const serviceCards = document.getElementById('service-cards');
    if (serviceCards) {
      serviceCards.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  // Schema markup
  const localBusinessSchema = createLocalBusinessSchema('Denver Metro');
  
  // Organization Schema for E-E-A-T
  const organizationSchema = {
    "@context": "https://schema.org",
    "@type": "Organization",
    "name": "Colorado First Plumbing and Sewer LLC",
    "url": "https://colorado-plumbing.com",
    "logo": "https://colorado-plumbing.com/colorado-plumbing-uploads/brand/colorado-first-plumbing-backgroundless-logo-800w.webp",
    "address": {
      "@type": "PostalAddress",
      "streetAddress": "5108 Sydney Ave",
      "addressLocality": "Littleton",
      "addressRegion": "CO",
      "postalCode": "80130"
    },
    "sameAs": [
      "https://www.facebook.com/coloradoplumbing/",
      "https://g.co/kgs/xd3PznG",
      "https://www.tiktok.com/@milehighplumber",
      "https://www.youtube.com/@bestmasterplumber",
      "https://www.instagram.com/coloradofirstplumbing/",
      "https://birdeye.com/colorado-first-plumbing-and-sewer-llc-172384402926614"
    ],
    "aggregateRating": {
      "@type": "AggregateRating",
      "ratingValue": "4.8",
      "reviewCount": "360",
      "bestRating": "5"
    }
  };

  // WebPage Schema
  const webPageSchema = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "name": "Upfront Plumbing Prices Denver - Pricing Philosophy",
    "description": "Learn why Colorado First Plumbing is the only Denver Metro plumber showing exact, upfront prices online. No hidden fees, no salespeople.",
    "url": "https://colorado-plumbing.com/upfront-plumbing-prices-denver",
    "breadcrumb": {
      "@type": "BreadcrumbList",
      "itemListElement": [
        {
          "@type": "ListItem",
          "position": 1,
          "name": "Home",
          "item": "https://colorado-plumbing.com"
        },
        {
          "@type": "ListItem",
          "position": 2,
          "name": "Pricing Philosophy",
          "item": "https://colorado-plumbing.com/upfront-plumbing-prices-denver"
        }
      ]
    }
  };

  // FAQ Schema
  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": [
      {
        "@type": "Question",
        "name": "What's the price of a 50 Gallon standard water heater out-the-door in Lakewood?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Colorado First Plumbing and Sewer LLC offers transparent flat-rate pricing of $2,100 for a 50-gallon standard atmospheric water heater fully installed in Lakewood. This includes the Rheem or AO Smith unit, expansion tank, new supply lines, and all code upgrades. No hidden fees or surprise charges."
        }
      },
      {
        "@type": "Question",
        "name": "How much for a Navien tankless replacement in Highlands Ranch?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "$5,000 for a complete Navien NPE-240A tankless water heater replacement in Highlands Ranch. This flat-rate price includes removal of your old tankless unit, installation of the new condensing system with 96% efficiency"
        }
      },
      {
        "@type": "Question",
        "name": "What does spot sewer repair cost in Arvada yards?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Spot sewer repair in Arvada yards costs $5,000 flat-rate with Colorado First Plumbing. This includes excavation, repair of the damaged section, backfill, and restoration. For under-slab repairs, $2,500 for 5 feet and $5,000 for 10 feet."
        }
      },
      {
        "@type": "Question",
        "name": "Price for professional drain cleaning in Castle Rock?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Professional drain cleaning in Castle Rock starts at $225 for traditional snake service and goes up to $795 for complex hydro jetting. Colorado First Plumbing shows all prices online before you call, so you know exactly what you'll pay."
        }
      },
      {
        "@type": "Question",
        "name": "Cost to remove roots from sewer line in Denver?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Root removal from sewer lines in Denver costs $495-$795 depending on severity. Basic cutting is $495, while complex removal requiring hydro jetting is $795. All prices are flat-rate with no hidden fees at Colorado First Plumbing."
        }
      },
      {
        "@type": "Question",
        "name": "What's hydro jetting service pricing in Littleton?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Hydro jetting service in Littleton is priced at $695 for standard residential lines and $1,295 for complex commercial or heavily blocked systems. Colorado First Plumbing provides upfront pricing with no surprise charges."
        }
      },
      {
        "@type": "Question",
        "name": "Installation cost for new disposal in Centennial?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Garbage disposal installation in Centennial ranges from $350 for basic 1/3 HP units to $650 for premium 1 HP models with advanced features."
        }
      },
      {
        "@type": "Question",
        "name": "How much for leak detection service in Longmont?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Leak detection service in Longmont costs $95 for standard diagnosis or $225 for difficult diagnosis requiring thermal imaging or advanced equipment. Colorado First Plumbing provides transparent diagnostic pricing with no hidden service charges."
        }
      }
    ]
  };

  // Combined schemas for injection
  const schemas = [
    organizationSchema,
    localBusinessSchema,
    webPageSchema,
    faqSchema
  ];

  return (
    <div className="min-h-screen">
      <SchemaInjector schemas={schemas} />
      <Header />
      
      <main>
        {/* Hero Section */}
        <section className="relative pt-40 pb-20 bg-gradient-to-br from-blue-800 to-blue-900 overflow-hidden">
          <div className="absolute inset-0 opacity-10 bg-[radial-gradient(#FFFFFF_1px,transparent_1px)] [background-size:32px_32px]"></div>
          <div className="container mx-auto px-4 relative z-10">
            <div className="max-w-4xl mx-auto text-center">
              {/* Trust Signals Banner */}
              <div className="inline-block px-6 py-3 mb-6 rounded-full bg-white/10 backdrop-blur-sm border border-white/20">
                <span className="text-white font-bold">🔥 No Hidden Fees • No Salespeople • Just Honest Prices • 1000+ Reviews Across All Platforms</span>
              </div>
              
              <h1 className="text-4xl md:text-6xl font-bold mb-6 text-white leading-tight">
                Finally, a Denver Plumber with Upfront, Honest Pricing Online
              </h1>
              
              <p className="text-xl text-blue-200 mb-8 max-w-3xl mx-auto">
                Think about how you shop for anything else. If you need a new TV or even just groceries, you can see the price before you buy. Why should hiring a plumber in the Denver metro area be any different? At Colorado First Plumbing and Sewer, we believe it shouldn't be. We are committed to being the affordable plumber near you that provides clarity, not confusion.
              </p>
              
              {/* Shortcut Button */}
              <button
                onClick={scrollToServiceCards}
                className="bg-gradient-to-r from-yellow-400 to-orange-400 hover:from-yellow-300 hover:to-orange-300 text-black px-8 py-4 rounded-full font-bold text-lg shadow-xl hover:shadow-2xl transform hover:scale-[1.02] transition-all duration-300 inline-flex items-center"
              >
                <DollarSign size={24} className="mr-2" />
                See Our Service Prices Now
                <ChevronDown size={24} className="ml-2 animate-bounce" />
              </button>
            </div>
          </div>
        </section>

        {/* Trust Cards Section */}
        <section className="py-12 bg-gradient-to-br from-blue-900 to-blue-950">
          <div className="container mx-auto px-4">
            <h2 className="text-3xl md:text-4xl font-bold text-center mb-12 text-white">
              Why Denver Metro Trusts Our Transparent Pricing Philosophy
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6">
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/20 text-center">
                <div className="text-4xl font-bold text-yellow-400 mb-2">18,000+</div>
                <div className="text-lg font-semibold text-white mb-2">Jobs Completed</div>
                <p className="text-blue-100 text-sm">Since 2018 serving Denver Metro with upfront, honest pricing on every job</p>
              </div>
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/20 text-center">
                <div className="text-4xl font-bold text-green-400 mb-2">1000+</div>
                <div className="text-lg font-semibold text-white mb-2">Reviews Online</div>
                <p className="text-blue-100 text-sm">4.8★ average across Google (360+), BirdEye (300+), HomeAdvisor (92+)</p>
              </div>
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/20 text-center">
                <div className="text-4xl font-bold text-blue-400 mb-2">30+</div>
                <div className="text-lg font-semibold text-white mb-2">Licensed Jurisdictions</div>
                <p className="text-blue-100 text-sm">Licensed throughout Denver Metro including Littleton, Arvada, and Highlands Ranch</p>
              </div>
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/20 text-center">
                <div className="text-4xl font-bold text-purple-400 mb-2">A+</div>
                <div className="text-lg font-semibold text-white mb-2">BBB Rating</div>
                <p className="text-blue-100 text-sm">Accredited 7/15/2024 with commitment to transparent business practices</p>
              </div>
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/20 text-center">
                <div className="text-4xl font-bold text-orange-400 mb-2">ONLY</div>
                <div className="text-lg font-semibold text-white mb-2">Licensed Plumber</div>
                <p className="text-blue-100 text-sm">In Colorado showing exact prices online with 1000+ verified reviews across all platforms</p>
              </div>
            </div>
          </div>
        </section>

        {/* The Industry's Hidden Secret Section */}
        <section className="py-16 bg-gradient-to-bl from-green-800 to-green-900">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-8 text-center">
                The Industry's Hidden Secret: The High-Pressure "In-Home Estimate"
              </h2>
              
              <div className="bg-white/10 backdrop-blur-sm rounded-3xl p-8 border border-white/20">
                <p className="text-lg text-green-100 mb-6">
                  For decades, the standard for most plumbing companies in Colorado has been to keep you, the homeowner, in the dark. Their entire business model is often built on hiding the price until they have a commissioned salesperson in your home.
                </p>
                
                <div className="space-y-4 mb-8">
                  <h3 className="text-xl font-bold text-white mb-4">The process is designed to put you at a disadvantage:</h3>
                  
                  <div className="flex items-start">
                    <AlertCircle size={24} className="text-yellow-300 mr-3 flex-shrink-0 mt-1" />
                    <p className="text-green-100">
                      You have an urgent problem, like a broken water heater in your Littleton home or a clogged drain in Highlands Ranch.
                    </p>
                  </div>
                  
                  <div className="flex items-start">
                    <AlertCircle size={24} className="text-yellow-300 mr-3 flex-shrink-0 mt-1" />
                    <p className="text-green-100">
                      You search for a plumber and call a company that advertises "fair" prices (without showing you a single one).
                    </p>
                  </div>
                  
                  <div className="flex items-start">
                    <AlertCircle size={24} className="text-yellow-300 mr-3 flex-shrink-0 mt-1" />
                    <p className="text-green-100">
                      They dispatch a technician—who is often a trained salesperson—to your home.
                    </p>
                  </div>
                  
                  <div className="flex items-start">
                    <AlertCircle size={24} className="text-yellow-300 mr-3 flex-shrink-0 mt-1" />
                    <p className="text-green-100">
                      Only then, under pressure, are you shown a price. When you're searching for the Denver water heater cost, you shouldn't have to wait for a salesperson to give you a quote that can be inflated on the spot.
                    </p>
                  </div>
                </div>
                
                <div className="bg-green-900/50 rounded-xl p-6 border-l-4 border-yellow-400">
                  <p className="text-lg text-white font-semibold">
                    This system leverages your urgency against you. The homeowner in Arvada with a sudden leak or the family in Centennial whose sewer line backs up is pressured into a snap decision, often paying far more than they should for a repair.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* "Affordable" Means Nothing Section */}
        <section className="py-16 bg-gradient-to-br from-purple-800 to-purple-900">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-8 text-center">
                "Affordable" and "Honest" Mean Nothing Without a Real Price Tag
              </h2>
              
              <div className="bg-white/10 backdrop-blur-sm rounded-3xl p-8 border border-white/20">
                <p className="text-lg text-purple-100 mb-6">
                  When companies hide their costs, phrases like "honest plumber in Denver" become empty marketing slogans. If a price is truly fair, why hide it? This practice is why so many people search for "how much does a sewer replacement in Arvada" or "what does a tankless water heater cost in Lakewood" only to find vague ranges instead of real numbers.
                </p>
                
                <div className="bg-purple-900/50 rounded-xl p-6 mb-6">
                  <h3 className="text-xl font-bold text-yellow-300 mb-4">The Truth About "Big Box" Plumbing Companies:</h3>
                  <p className="text-purple-100 mb-4">
                    The large, "Big Box" plumbing companies have perfected this system. They know their numbers. They intentionally conceal them to protect a business model that relies on high-pressure sales to fund their massive overhead.
                  </p>
                  <p className="text-purple-100">
                    Don't just take our word for it. Search job sites for "Plumbing Sales Advisor" or "Residential Sewer Salesman" in the Denver area. You'll find commission-based salaries ranging from <span className="text-yellow-300 font-bold">$85,000 to over $240,000</span>. These are the "experts" sent to your home to maximize profit. Their priority is a sale, not providing you with the most affordable plumbing solution.
                  </p>
                </div>
                
                <div className="flex items-center justify-center">
                  <TrendingUp size={48} className="text-yellow-300 mr-4" />
                  <div className="text-center">
                    <p className="text-2xl font-bold text-white">Commission-Based "Plumbers"</p>
                    <p className="text-lg text-purple-100">$85,000 - $240,000+ Salaries</p>
                    <p className="text-sm text-purple-200">Paid to maximize your bill, not solve your problem</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Our Solution Section */}
        <section className="py-16 bg-gradient-to-bl from-blue-800 to-blue-900">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-8 text-center">
                Our Solution: Transparent Plumbing and Sewer Services for Colorado
              </h2>
              
              <div className="bg-white/10 backdrop-blur-sm rounded-3xl p-8 border border-white/20">
                <p className="text-lg text-blue-100 mb-8">
                  At Colorado First Plumbing and Sewer, we are here to change the status quo. We believe in earning your business through trust, not tactics.
                </p>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
                  <div className="bg-blue-900/50 rounded-xl p-6 border-l-4 border-yellow-400">
                    <h3 className="text-xl font-bold text-white mb-3 flex items-center">
                      <DollarSign size={24} className="mr-2 text-yellow-300" />
                      Upfront, Online Pricing
                    </h3>
                    <p className="text-blue-100">
                      You can see the cost before you call. Whether you need upfront priced water heaters in Littleton or a transparent quote for a faucet replacement in Denver, you'll find it on our site. No surprises.
                    </p>
                  </div>
                  
                  <div className="bg-blue-900/50 rounded-xl p-6 border-l-4 border-green-400">
                    <h3 className="text-xl font-bold text-white mb-3 flex items-center">
                      <UserCheck size={24} className="mr-2 text-green-300" />
                      Expert Technicians, Not Salespeople
                    </h3>
                    <p className="text-blue-100">
                      Our licensed plumbers are rewarded for quality work and your satisfaction. They are here to be the honest plumber you can rely on, not to push a sale.
                    </p>
                  </div>
                  
                  <div className="bg-blue-900/50 rounded-xl p-6 border-l-4 border-blue-400">
                    <h3 className="text-xl font-bold text-white mb-3 flex items-center">
                      <FileText size={24} className="mr-2 text-blue-300" />
                      Informed Decisions, No Hassle
                    </h3>
                    <p className="text-blue-100">
                      We provide the information you need to make the right choice for your home and budget, pressure-free.
                    </p>
                  </div>
                  
                  <div className="bg-blue-900/50 rounded-xl p-6 border-l-4 border-purple-400">
                    <h3 className="text-xl font-bold text-white mb-3 flex items-center">
                      <Star size={24} className="mr-2 text-purple-300" />
                      Proven Quality & Trust
                    </h3>
                    <p className="text-blue-100">
                      We use the same high-quality materials and hold the same licenses and insurance as the big companies. Our reputation speaks for itself.
                    </p>
                  </div>
                </div>
                
                <div className="bg-gradient-to-r from-blue-800 to-blue-900 rounded-xl p-6 text-center">
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                    <div>
                      <p className="text-3xl font-bold text-yellow-300">4.8★</p>
                      <p className="text-sm text-blue-100">Google (360+ Reviews)</p>
                    </div>
                    <div>
                      <p className="text-3xl font-bold text-green-300">4.8★</p>
                      <p className="text-sm text-blue-100">BirdEye (300+ Reviews)</p>
                    </div>
                    <div>
                      <p className="text-3xl font-bold text-blue-300">4.8★</p>
                      <p className="text-sm text-blue-100">HomeAdvisor (92+ Reviews)</p>
                    </div>
                    <div>
                      <p className="text-3xl font-bold text-purple-300">1000+</p>
                      <p className="text-sm text-blue-100">5-Star Ratings Total</p>
                    </div>
                  </div>
                  <p className="text-lg text-white font-semibold">
                    You shouldn't have to guess the cost of plumbing repairs in Denver. With Colorado First Plumbing and Sewer, you don't have to. You can finally make a truly informed, no-hassle decision for your plumbing needs. The power is back in your hands.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Service Pricing Cards Section */}
        <section id="service-cards" className="py-16 bg-gradient-to-br from-blue-900 to-blue-950">
          <div className="container mx-auto px-4">
            <h2 className="text-3xl md:text-4xl font-bold text-white text-center mb-4">Our Plumbing Service Prices</h2>
            <p className="text-xl text-blue-200 text-center mb-12 max-w-3xl mx-auto">
              Click any service below to see exact, upfront pricing. No hidden fees. No surprise charges. Just honest prices for quality work.
            </p>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-7xl mx-auto">
              {/* Sewer & Drain Cleaning Card */}
              <div className="relative bg-white/10 backdrop-blur-sm rounded-3xl overflow-hidden border border-white/20 hover:shadow-2xl hover:scale-[1.02] transition-all duration-300 group">
                <div 
                  className="absolute inset-0 bg-cover bg-center transition-all duration-300 group-hover:scale-105"
                  style={{
                    backgroundImage: 'url(/colorado-plumbing-uploads/locations/aurora/drain-clean-vent-roof-aurora.JPEG)',
                    filter: 'brightness(0.4)'
                  }}
                />
                <div className="absolute inset-0 bg-gradient-to-br from-green-800/70 to-green-900/70" />
                
                <div className="relative z-10 p-8">
                  <div className="w-16 h-16 bg-white/20 backdrop-blur-sm rounded-lg flex items-center justify-center mb-4">
                    <Droplet size={32} className="text-green-300" />
                  </div>
                  <h3 className="text-2xl font-bold text-white mb-3">Sewer & Drain Cleaning Prices</h3>
                  <p className="text-green-100 mb-6">
                    From routine drain cleaning in Highlands Ranch to camera inspections, see our clear, upfront costs for all sewer and drain services. We handle clogs, roots, and more.
                  </p>
                  <div className="space-y-2 mb-6">
                    <p className="text-white font-semibold">• Traditional Snake: $225</p>
                    <p className="text-white font-semibold">• Hydro Jetting: $695</p>
                    <p className="text-white font-semibold">• Root Removal: $495-$795</p>
                  </div>
                  <div className="space-y-3">
                    <Link 
                      to="/sewer-cleaning-pricing"
                      className="bg-gradient-to-r from-green-400 to-green-500 hover:from-green-500 hover:to-green-600 text-white px-6 py-3 rounded-full font-bold transition-all duration-300 hover:shadow-lg text-center block"
                    >
                      See All Sewer & Drain Cleaning Prices →
                    </Link>
                    <div className="grid grid-cols-2 gap-2">
                      <Link to="/root-removal-pricing" className="text-green-200 hover:text-white text-sm underline">Root Removal</Link>
                      <Link to="/hydro-jetting-pricing" className="text-green-200 hover:text-white text-sm underline">Hydro Jetting</Link>
                      <Link to="/sewer-snake-auger-pricing" className="text-green-200 hover:text-white text-sm underline">Snake/Auger</Link>
                      <Link to="/cast-iron-descaling-pricing" className="text-green-200 hover:text-white text-sm underline">Descaling</Link>
                    </div>
                  </div>
                </div>
              </div>

              {/* Sewer Repair & Replace Card */}
              <div className="relative bg-white/10 backdrop-blur-sm rounded-3xl overflow-hidden border border-white/20 hover:shadow-2xl hover:scale-[1.02] transition-all duration-300 group">
                <div 
                  className="absolute inset-0 bg-cover bg-center transition-all duration-300 group-hover:scale-105"
                  style={{
                    backgroundImage: 'url(/page-specific-content/arvada-plumber/arvada-sewer-excavator-replacement.jpg)',
                    filter: 'brightness(0.4)'
                  }}
                />
                <div className="absolute inset-0 bg-gradient-to-br from-purple-800/70 to-purple-900/70" />
                
                <div className="relative z-10 p-8">
                  <div className="w-16 h-16 bg-white/20 backdrop-blur-sm rounded-lg flex items-center justify-center mb-4">
                    <Wrench size={32} className="text-purple-300" />
                  </div>
                  <h3 className="text-2xl font-bold text-white mb-3">Sewer Repair & Replace Pricing</h3>
                  <p className="text-purple-100 mb-6">
                    Complete transparency on sewer line repairs and replacements in Arvada, Littleton, and beyond. From spot repairs to full replacements.
                  </p>
                  <div className="space-y-2 mb-6">
                    <p className="text-white font-semibold">• Spot Repair (Yard): $5,000</p>
                    <p className="text-white font-semibold">• Under-Slab: $2,500-$5,000</p>
                    <p className="text-white font-semibold">• Full Replace: $12,000</p>
                  </div>
                  <Link 
                    to="/sewer-replacement-spot-repair-pricing"
                    className="bg-gradient-to-r from-purple-400 to-purple-500 hover:from-purple-500 hover:to-purple-600 text-white px-6 py-3 rounded-full font-bold transition-all duration-300 hover:shadow-lg text-center block"
                  >
                    See All Sewer Repair Prices →
                  </Link>
                </div>
              </div>

              {/* Water Heater Pricing Card */}
              <div className="relative bg-white/10 backdrop-blur-sm rounded-3xl overflow-hidden border border-white/20 hover:shadow-2xl hover:scale-[1.02] transition-all duration-300 group">
                <div 
                  className="absolute inset-0 bg-cover bg-center transition-all duration-300 group-hover:scale-105"
                  style={{
                    backgroundImage: 'url(/page-specific-content/highlands-ranch-plumber/water-heater-replaced-in-highlands-ranch (6).jpg)',
                    filter: 'brightness(0.4)'
                  }}
                />
                <div className="absolute inset-0 bg-gradient-to-br from-blue-800/70 to-blue-900/70" />
                
                <div className="relative z-10 p-8">
                  <div className="w-16 h-16 bg-white/20 backdrop-blur-sm rounded-lg flex items-center justify-center mb-4">
                    <Flame size={32} className="text-blue-300" />
                  </div>
                  <h3 className="text-2xl font-bold text-white mb-3">Water Heater Pricing</h3>
                  <p className="text-blue-100 mb-6">
                    Exact water heater installation costs for Lakewood, Denver, and surrounding areas. Standard atmospheric and power vent options available.
                  </p>
                  <div className="space-y-2 mb-6">
                    <p className="text-white font-semibold">• 40 Gallon Atmospheric Rheem: $2,000</p>
                    <p className="text-white font-semibold">• 50 Gallon Bradford White Standard: $2,400</p>
                    <p className="text-white font-semibold">• Power Vent: $4,000+</p>
                    <p className="text-white text-sm mt-2">More price options on page</p>
                  </div>
                  <div className="space-y-3">
                    <Link 
                      to="/water-heater-pricing"
                      className="bg-gradient-to-r from-blue-400 to-blue-500 hover:from-blue-500 hover:to-blue-600 text-white px-6 py-3 rounded-full font-bold transition-all duration-300 hover:shadow-lg text-center block"
                    >
                      See All Water Heater Prices →
                    </Link>
                    <Link to="/flat-rate-affordable-rheem-water-heaters-in-colorado" className="text-blue-200 hover:text-white text-sm underline text-center block">
                      💰 $2,000-$2,100 Rheem Special Deal
                    </Link>
                  </div>
                </div>
              </div>

              {/* Tankless Water Heaters Card */}
              <div className="relative bg-white/10 backdrop-blur-sm rounded-3xl overflow-hidden border border-white/20 hover:shadow-2xl hover:scale-[1.02] transition-all duration-300 group">
                <div 
                  className="absolute inset-0 bg-cover bg-center transition-all duration-300 group-hover:scale-105"
                  style={{
                    backgroundImage: 'url(/page-specific-content/littleton-plumber/tankless-water-heater-professional-replacement-centennial.jpeg)',
                    filter: 'brightness(0.4)'
                  }}
                />
                <div className="absolute inset-0 bg-gradient-to-br from-orange-800/70 to-orange-900/70" />
                
                <div className="relative z-10 p-8">
                  <div className="w-16 h-16 bg-white/20 backdrop-blur-sm rounded-lg flex items-center justify-center mb-4">
                    <Thermometer size={32} className="text-orange-300" />
                  </div>
                  <h3 className="text-2xl font-bold text-white mb-3">Tankless Water Heater Pricing</h3>
                  <p className="text-orange-100 mb-6">
                    Transparent pricing for Navien and Rinnai tankless systems in Castle Rock, Centennial, and Denver Metro. Endless hot water solutions.
                  </p>
                  <div className="space-y-2 mb-6">
                    <p className="text-white font-semibold">• Replacement: $5,000</p>
                    <p className="text-white font-semibold">• New Install: $8,000</p>
                    <p className="text-white font-semibold">• Both Navien & Rinnai</p>
                  </div>
                  <Link 
                    to="/tankless-water-heater-pricing"
                    className="bg-gradient-to-r from-orange-400 to-orange-500 hover:from-orange-500 hover:to-orange-600 text-white px-6 py-3 rounded-full font-bold transition-all duration-300 hover:shadow-lg text-center block"
                  >
                    See Tankless Water Heater Prices →
                  </Link>
                </div>
              </div>

              {/* Kitchen Plumbing Card */}
              <div className="relative bg-white/10 backdrop-blur-sm rounded-3xl overflow-hidden border border-white/20 hover:shadow-2xl hover:scale-[1.02] transition-all duration-300 group">
                <div 
                  className="absolute inset-0 bg-cover bg-center transition-all duration-300 group-hover:scale-105"
                  style={{
                    backgroundImage: 'url(/colorado-plumbing-uploads/locations/castle-rock/sink-filter-install-castle-rock.JPEG)',
                    filter: 'brightness(0.4)'
                  }}
                />
                <div className="absolute inset-0 bg-gradient-to-br from-yellow-800/70 to-yellow-900/70" />
                
                <div className="relative z-10 p-8">
                  <div className="w-16 h-16 bg-white/20 backdrop-blur-sm rounded-lg flex items-center justify-center mb-4">
                    <Home size={32} className="text-yellow-300" />
                  </div>
                  <h3 className="text-2xl font-bold text-white mb-3">Kitchen Plumbing Prices</h3>
                  <p className="text-yellow-100 mb-6">
                    Upfront pricing for kitchen faucets and garbage disposals in Longmont, Littleton, and throughout Denver Metro. Professional installation guaranteed.
                  </p>
                  <div className="space-y-2 mb-6">
                    <p className="text-white font-semibold">• Disposal: $350-$650</p>
                    <p className="text-white font-semibold">• Faucet: $225-$550</p>
                    <p className="text-white font-semibold">• Same-day available</p>
                  </div>
                  <div className="space-y-3">
                    <Link 
                      to="/garbage-disposal-pricing"
                      className="bg-gradient-to-r from-yellow-400 to-yellow-500 hover:from-yellow-500 hover:to-yellow-600 text-white px-6 py-3 rounded-full font-bold transition-all duration-300 hover:shadow-lg text-center block"
                    >
                      See Kitchen Plumbing Prices →
                    </Link>
                    <Link to="/faucet-pricing" className="text-yellow-200 hover:text-white text-sm underline text-center block">
                      View Faucet Installation Prices
                    </Link>
                  </div>
                </div>
              </div>

              {/* Diagnostic Services Card */}
              <div className="relative bg-white/10 backdrop-blur-sm rounded-3xl overflow-hidden border border-white/20 hover:shadow-2xl hover:scale-[1.02] transition-all duration-300 group">
                <div 
                  className="absolute inset-0 bg-cover bg-center transition-all duration-300 group-hover:scale-105"
                  style={{
                    backgroundImage: 'url(/page-specific-content/castle-rock-plumber/thermal-camera-leak-detection-castle-rock.JPEG)',
                    filter: 'brightness(0.4)'
                  }}
                />
                <div className="absolute inset-0 bg-gradient-to-br from-red-800/70 to-red-900/70" />
                
                <div className="relative z-10 p-8">
                  <div className="w-16 h-16 bg-white/20 backdrop-blur-sm rounded-lg flex items-center justify-center mb-4">
                    <Search size={32} className="text-red-300" />
                  </div>
                  <h3 className="text-2xl font-bold text-white mb-3">Diagnostic Services Pricing</h3>
                  <p className="text-red-100 mb-6">
                    Transparent diagnostic and leak detection pricing for Englewood, Longmont, and all Denver Metro. Thermal imaging and camera inspection available.
                  </p>
                  <div className="space-y-2 mb-6">
                    <p className="text-white font-semibold">• Standard: $95</p>
                    <p className="text-white font-semibold">• Difficult: $225</p>
                    <p className="text-white font-semibold">• Annual Checkup: $350</p>
                  </div>
                  <Link 
                    to="/diagnostic-services-pricing"
                    className="bg-gradient-to-r from-red-400 to-red-500 hover:from-red-500 hover:to-red-600 text-white px-6 py-3 rounded-full font-bold transition-all duration-300 hover:shadow-lg text-center block"
                  >
                    See Diagnostic Service Prices →
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Master Plumber Attribution */}
        <section className="py-16 bg-gradient-to-br from-blue-800 to-blue-900">
          <div className="container mx-auto px-4 max-w-4xl">
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-8 border border-white/20">
              <div className="flex flex-col md:flex-row items-center gap-8">
                <img
                  src="/colorado-plumbing-uploads/brand/codee-christian-plumbing-truck.png"
                  alt="Codee Jessen - Master Plumber with Colorado First Plumbing service truck"
                  className="w-32 h-32 rounded-full object-cover bg-white/5 border-4 border-white/20 shadow-lg"
                />
                <div className="flex-grow text-center md:text-left">
                  <h3 className="text-2xl font-bold text-white mb-1">Master Plumber - Transparency Advocate</h3>
                  <h4 className="text-xl font-semibold text-yellow-300 mb-2">Codee Jessen</h4>
                  <p className="text-sm text-blue-200 mb-4 italic">
                    "Ever wonder why you can't find plumbing prices online? It's a strategy. Most companies want to get a commissioned salesperson into your home to use high-pressure tactics. We believe that's wrong. You deserve to see the product, the proof of our quality, and the price—all before you ever pick up the phone."
                  </p>
                  <div className="text-blue-100 mb-4">
                    <p>• Philosophy: No games, no hidden fees, no salespeople</p>
                    <p>• Experience: 18,000+ jobs with transparent pricing</p>
                    <p>• Commitment: The ONLY plumber showing exact prices online</p>
                  </div>
                  <Link to="/team/codee-jessen" className="text-yellow-300 hover:text-yellow-200 font-semibold">
                    Learn more about our Master Plumber →
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="py-16 bg-gradient-to-bl from-green-800 to-green-900">
          <div className="container mx-auto px-4">
            <h2 className="text-3xl md:text-4xl font-bold text-white text-center mb-12">
              Common Pricing Questions From Denver Metro Homeowners
            </h2>
            <div className="max-w-4xl mx-auto space-y-6">
              {faqSchema.mainEntity.map((faq, index) => (
                <div key={index} className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/20">
                  <h3 className="text-xl font-bold text-white mb-3 flex items-start">
                    <span className="text-yellow-300 mr-3">Q:</span>
                    {faq.name}
                  </h3>
                  <p className="text-green-100 pl-8">
                    <span className="text-green-300 font-bold">A:</span> {faq.acceptedAnswer.text}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Contact Section */}
        <section className="py-20 bg-gradient-to-br from-gray-900 to-gray-800">
          <div className="container mx-auto px-4">
            <div className="text-center mb-16">
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
                Ready to Experience Honest, Upfront Pricing?
              </h2>
              <p className="text-xl text-gray-300 max-w-3xl mx-auto">
                No salespeople. No hidden fees. Just skilled plumbers with transparent prices. Contact us today for service you can trust.
              </p>
            </div>
            
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 max-w-6xl mx-auto">
              {/* Contact Form */}
              <div>
                <ContactForm />
              </div>
              
              {/* Contact Info */}
              <div>
                <ContactInfo />
                
                {/* BirdEye Widget Container */}
                <div className="mt-8">
                  <div id="bf-revz-widget-172384402926614"></div>
                  <div id="birdeye-fallback" style={{ display: 'none' }} className="bg-white/10 rounded-lg p-6 text-center">
                    <p className="text-white mb-4">⭐⭐⭐⭐⭐</p>
                    <p className="text-white font-semibold">4.8 Stars • 1000+ Reviews Across All Platforms</p>
                    <a 
                      href="https://birdeye.com/colorado-first-plumbing-and-sewer-llc-172384402926614" 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="text-blue-300 hover:text-blue-200 underline mt-2 inline-block"
                    >
                      View Our Reviews on BirdEye
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>
      
      <Footer />
    </div>
  );
};

export default UpfrontPlumbingPricesDenver;